import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Coins, Sparkle, WhatsappLogo, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";

const QUICK_AMOUNTS = [50, 100, 250, 500, 1000, 5000];

export default function BuyPoints() {
  const { user, refresh } = useAuth();
  const [amount, setAmount] = useState(100);
  const [submitting, setSubmitting] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [paystack, setPaystack] = useState({ configured: false, currency: "GHS", public_key: "" });

  const load = useCallback(async () => {
    try {
      const [w, p, ps] = await Promise.all([
        api.get("/wallet/summary"),
        api.get("/me/point-purchases"),
        api.get("/paystack/config").catch(() => ({ data: { configured: false } })),
      ]);
      setWallet(w.data);
      setPurchases(p.data);
      setPaystack(ps.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const points = parseInt(amount, 10);
    if (!points || points <= 0) {
      toast.error("Enter a valid number of Credits");
      return;
    }
    // Per current architecture: the "Pay" button always deep-links to the
    // admin's WhatsApp so any future payment gateway (Paystack, Flutterwave,
    // Stripe with Crypto…) can safely replace this link when ready.
    // We still register the intent server-side so admin sees the request.
    setSubmitting(true);
    try {
      await api.post("/points/buy-request", { points }).catch(() => null);
    } finally {
      setSubmitting(false);
    }
    const bundleMsg = encodeURIComponent(
      `Hi Admin, I want to buy ${points} Tain Credits (about GHS ${total.toFixed(2)}). ` +
      `My Tain email: ${user?.email || ""}. Please confirm the payment steps.`
    );
    window.open(`https://wa.me/233539025750?text=${bundleMsg}`,
                 "_blank", "noopener");
    load();
  };

  const price = wallet?.point_price ?? 0.05;
  const total = (parseInt(amount, 10) || 0) * price;

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto space-y-6" data-testid="buy-points-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black flex items-center gap-3">
          <Coins size={32} weight="fill" className="text-yellow-400" /> Buy Credits
        </h1>
        <p className="text-white/60 mt-1">
          Pre-paid Platform Credits power every interaction on Tain — like,
          comment, save, share, gift, upload and unlock premium modules.
        </p>
      </div>

      {wallet && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Available" value={wallet.points} highlight />
          <Stat label="Purchased" value={wallet.points_purchased_total} />
          <Stat label="Spent" value={wallet.points_spent_total} />
          <Stat label="Earned" value={wallet.points_earned_total} />
        </div>
      )}

      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-yellow-300" /> New Credit Bundle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 font-bold">Credits to buy</label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="buy-quick-amounts">
              {QUICK_AMOUNTS.map((a) => (
                <button
                  key={a}
                  data-testid={`buy-amount-${a}`}
                  onClick={() => setAmount(a)}
                  className={`px-3 py-2 rounded-md text-sm font-heading font-bold ${
                    parseInt(amount, 10) === a
                      ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-black shadow-lg"
                      : "bg-white/5 text-white/70 hover:bg-white/10"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
            <Input
              data-testid="buy-custom-amount"
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-white/5 border-white/10 text-white mt-2"
            />
          </div>

          <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
            <div className="flex items-baseline justify-between">
              <p className="text-xs uppercase tracking-widest font-bold text-yellow-300">Total to pay</p>
              <p className="text-[11px] text-white/50">${price.toFixed(4)}/cr</p>
            </div>
            <p className="font-heading text-4xl font-black text-white mt-1">${total.toFixed(2)}</p>
          </div>

          <Button
            data-testid="buy-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="w-full h-12 bg-gradient-to-r from-yellow-500 to-orange-500 hover:brightness-110 text-black font-heading font-bold text-base"
          >
            {submitting ? "Opening WhatsApp…" : `Pay — ${amount || 0} Credits`}
          </Button>

          <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70">
            <p className="font-bold text-white mb-1">How it works</p>
            <ol className="list-decimal list-inside space-y-1" data-testid="buy-flow">
              <li>Pick a bundle above or type your own amount.</li>
              <li>Hit <b>Pay</b> — a WhatsApp chat opens with your order prefilled.</li>
              <li>Complete payment with the admin.</li>
              <li>Credits land in your wallet as soon as admin confirms.</li>
            </ol>
          </div>

          <a
            data-testid="buy-whatsapp-btn"
            href="https://wa.me/233539025750"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 h-12 rounded-md bg-[#25D366] hover:bg-[#1DA851] text-white font-heading font-bold transition-colors"
          >
            <WhatsappLogo size={20} weight="fill" />
            Contact admin to pay
          </a>

          <p className="text-[11px] text-white/45 text-center pt-1">
            By purchasing you agree to our{" "}
            <Link to="/terms" data-testid="buy-terms-link" className="underline hover:text-white/80">
              Terms of Service
            </Link>. Credits have no cash value and are non-refundable.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-lg">My requests</CardTitle>
        </CardHeader>
        <CardContent>
          {purchases.length === 0 ? (
            <p className="text-white/50 text-sm text-center py-6">No purchases yet.</p>
          ) : (
            <div className="space-y-2" data-testid="my-purchases-list">
              {purchases.map((p) => (
                <div key={p.purchase_id} className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-center gap-3">
                  {p.status === "paid" && <CheckCircle size={20} weight="fill" className="text-emerald-400 shrink-0" />}
                  {p.status === "pending" && <Clock size={20} weight="fill" className="text-yellow-400 shrink-0" />}
                  {p.status === "rejected" && <XCircle size={20} weight="fill" className="text-red-400 shrink-0" />}
                  <div className="flex-1">
                    <p className="font-heading font-bold text-sm">{p.points} Credits — ${p.amount_usd.toFixed(2)}</p>
                    <p className="text-[11px] text-white/50">
                      {new Date(p.created_at).toLocaleString()} • {p.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-center text-xs text-white/40">
        <Link to="/dashboard" className="hover:text-white/70">Back to wallet →</Link>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`p-4 rounded-lg ${highlight ? "bg-gradient-to-br from-yellow-500/20 to-orange-500/20 ring-1 ring-yellow-400/40" : "bg-white/5 border border-white/10"}`}>
      <p className="text-[11px] uppercase tracking-widest text-white/60 font-bold">{label}</p>
      <p className="font-heading text-2xl font-black text-white mt-1">{value}</p>
    </div>
  );
}
